import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import { MatDateFormats } from '@angular/material/core';

/**
 * Adaptateur de date Material en français : saisie et affichage au format
 * `JJ-MM-AAAA`. Utilisé globalement via `MAT_DATE_FORMATS` + `DateAdapter`.
 * Le payload envoyé à l'API reste géré par les mappers (`AAAA-MM-JJ`).
 */
@Injectable()
export class FrDateAdapter extends NativeDateAdapter {
    /** Lundi comme premier jour de la semaine (calendrier FR). */
    override getFirstDayOfWeek(): number {
        return 1;
    }

    /**
     * `JJ-MM-AAAA` → Date. Renvoie une date invalide si non conforme.
     *
     * Le tiret, la barre oblique et le point sont acceptés indifféremment en saisie : au clavier,
     * `15/12/2026` est le geste naturel en français, et le refuser laissait le champ afficher la
     * date tout en gardant le modèle à `null` — formulaire bloqué sans explication visible.
     *
     * Les huit chiffres consécutifs sont acceptés eux aussi (`20122000`) : c'est la frappe la plus
     * rapide, sans quitter le pavé numérique. Le champ se reformate alors de lui-même en
     * `20-12-2000` à la sortie — `MatDatepickerInput._onBlur()` réécrit la valeur affichée dès
     * lors que le modèle est valide.
     *
     * L'affichage, lui, reste normalisé en `JJ-MM-AAAA` par `format()`.
     */
    override parse(value: unknown): Date | null {
        if (typeof value === 'string') {
            const saisie = value.trim();
            const m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(saisie) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(saisie);
            if (m) {
                const day = Number(m[1]);
                const month = Number(m[2]);
                const year = Number(m[3]);
                return this.createValidDate(year, month - 1, day);
            }
            return saisie ? new Date(NaN) : null;
        }
        if (typeof value === 'number') {
            return new Date(value);
        }
        return value ? new Date(value as string) : null;
    }

    /** Toujours afficher `JJ-MM-AAAA`. */
    override format(date: Date, _displayFormat: unknown): string {
        const d = String(date.getDate()).padStart(2, '0');
        const mo = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}-${mo}-${y}`;
    }

    /**
     * `JJ-MM-AAAA` également ici, par cohérence de format sur toute la surface de l'adaptateur.
     *
     * Material n'appelle cette méthode que pour les attributs `min` / `max` des champs de date
     * (`datepicker.mjs` : `[attr.min]` / `[attr.max]` du datepicker et des bornes de plage) ;
     * elle n'intervient ni dans la validation, assurée sur des `Date`, ni dans la valeur
     * transmise à l'API, produite par les mappers. Le projet ne l'appelle pas directement.
     */
    override toIso8601(date: Date): string {
        return this.format(date, null);
    }

    private createValidDate(year: number, month: number, day: number): Date {
        const date = new Date(year, month, day);
        // Rejette les dates « débordantes » (ex. 31-02) sans lever d'exception.
        if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
            return new Date(NaN);
        }
        return date;
    }
}

/** Formats Material : saisie + affichage en `JJ-MM-AAAA`. */
export const FR_DATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: 'dd-MM-yyyy',
    },
    display: {
        dateInput: 'dd-MM-yyyy',
        monthYearLabel: 'MMM yyyy',
        dateA11yLabel: 'dd-MM-yyyy',
        monthYearA11yLabel: 'MMMM yyyy',
    },
};
